import { Grid, Typography, Stack } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { Box } from "@mui/system";
import { useLazyQuery } from "@apollo/client";
import { GET_REPORT } from "../graphql/queries";
import logo from "../assets/NSCC_logo.png";
import altlogo from "../assets/SolverEDGE.svg";
import { useLocation, useParams } from "react-router-dom";
import RadarGraph from "../components/graphs/RadarGraph";
import ScatterGraph from "../components/graphs/ScatterGraph";
import BarGraph from "../components/graphs/BarGraph";
import RadarReportInstructions from "../components/RadarReportInstructions";
import ReadingReportInstructions from "../components/ReadingReportInstructions";
import BarReportInstructions from "../components/BarReportInstructions";
import { AuthenticatedContext } from "../App";
import TopicTranslations from "../utils/TopicTranslations";

const PlacementReport = () => {
  const [placementData, setPlacementData] = useState(null);
  const [overall, setOverall] = useState([]);
  const [todayDate, setTodayDate] = useState("");
  const [lComment, setLComment] = useState("");
  const [iComment, setIComment] = useState("");
  const [trainings, setTrainings] = useState([]);
  const [givenname, setGivenname] = useState("");
  const [studentName, setStudentName] = useState("");
  const [studentId, setStudentId] = useState(0);
  const [languageId, setLanguageId] = useState(0);

  const { user, userRole, getRole } = useContext(AuthenticatedContext);

  const location = useLocation();
  const { program, id } = useParams();

  const [getReport] = useLazyQuery(GET_REPORT, {
    onCompleted: (report) => {
      if (report.getReport.languageId) setLanguageId(report.getReport.languageId);
      if (report.getReport.trainings)
        setTrainings(report.getReport.trainings.split("|"));
      let sortedResults = {};
      const results = report.getReport.ps_results;
      const readingResults = results.filter((r) => r.psType === "Reading");

      let readingComponents = {};
      function reverseCompareScore(a, b) {
        return b.score - a.score;
      }
      const iTimes = readingResults
        .filter((r) => r.strand === "Inferential Analysis|Time")
        .sort(reverseCompareScore);
      const lTimes = readingResults
        .filter((r) => r.strand === "Literal Analysis|Time")
        .sort(reverseCompareScore);
      const iMarks = readingResults
        .filter((r) => r.strand === "Inferential Analysis|Mark")
        .sort(reverseCompareScore);
      const lMarks = readingResults
        .filter((r) => r.strand === "Literal Analysis|Mark")
        .sort(reverseCompareScore);

      const iTimeRank =
        (iTimes.findIndex((i) => i.studentId === report.getReport.Student.id) +
          1) /
        iTimes.length;
      const lTimeRank =
        (lTimes.findIndex((l) => l.studentId === report.getReport.Student.id) +
          1) /
        lTimes.length;
      const iMarkRank =
        (iMarks.findIndex((i) => i.studentId === report.getReport.Student.id) +
          1) /
        iMarks.length;
      const lMarkRank =
        (lMarks.findIndex((l) => l.studentId === report.getReport.Student.id) +
          1) /
        lMarks.length;

      if (iTimeRank < 0.2) {
        readingComponents["iTime"] = 1.5;
      } else if (iTimeRank <= 0.8) {
        readingComponents["iTime"] = 1;
      } else {
        readingComponents["iTime"] = 0.5;
      }
      if (lTimeRank < 0.2) {
        readingComponents["lTime"] = 2.5;
      } else if (lTimeRank <= 0.8) {
        readingComponents["lTime"] = 1.5;
      } else {
        readingComponents["lTime"] = 0.5;
      }
      if (iMarkRank < 0.2) {
        readingComponents["iMark"] = 3.5;
      } else if (iMarkRank <= 0.8) {
        readingComponents["iMark"] = 2;
      } else {
        readingComponents["iMark"] = 0.5;
      }
      if (lMarkRank < 0.2) {
        readingComponents["lMark"] = 2.5;
      } else if (lMarkRank <= 0.8) {
        readingComponents["lMark"] = 1.5;
      } else {
        readingComponents["lMark"] = 0.5;
      }
      readingComponents["inferential"] =
        readingComponents["iTime"] + readingComponents["iMark"];
      readingComponents["literal"] =
        readingComponents["lTime"] + readingComponents["lMark"];
      readingComponents["total"] =
        readingComponents["inferential"] + readingComponents["literal"];

      let newLComment = "";
      switch (readingComponents["literal"]) {
        case 1:
          if (report.getReport.languageId === 2) {
            newLComment = "閱讀速度相對於其他學生較慢；難以回答字面上的問題。";
          } else {
            newLComment = "Reading speed is slow relative to other students; difficulty experienced answering literal style of questions.";
          }
          break;
        case 2:
          if (readingComponents["lTime"] === 0.5) {
            if (report.getReport.languageId === 2) {
              newLComment = "閱讀速度相對於其他學生較慢；能夠回答字面上的問題。";
            } else {
              newLComment = "Reading speed is slow relative to other students; able to answer literal style of questions.";
            }
          } else {
            if (report.getReport.languageId === 2) {
              newLComment = "與其他學生相比，閱讀速度較快；回答字面上的問題時遇到困難。";
            } else {
              newLComment = "Good reading speed relative to other students; difficulty experienced answering literal style of questions.";
            }
          }
          break;
        case 3:
          switch (readingComponents["lTime"]) {
            case 0.5:
              if (report.getReport.languageId === 2) {
                newLComment = "閱讀速度相對於其他學生較慢；能夠自信地回答字面上的問題。";
              } else {
                newLComment = "Reading speed is slow relative to other students; confident to answer literal style of questions.";
              }
              break;
            case 1.5:
              if (report.getReport.languageId === 2) {
                newLComment = "與其他學生相比，閱讀速度較快；能夠回答字面上的問題。";
              } else {
                newLComment = "Good reading speed relative to other students; able to answer literal style of questions.";
              }
              break;
            case 2.5:
              if (report.getReport.languageId === 2) {
                newLComment = "與其他學生相比，閱讀速度快；回答字面意思的問題有困難。";
              } else {
                newLComment = "Fast reading speed relative to other students; difficulty experienced answering literal style of questions.";
              }
              break;
            default:
              if (report.getReport.languageId === 2) {
                newLComment = "讀取時間值異常。";
              } else {
                newLComment = "Unexpected reading time value.";
              }
          }
          break;
        case 4:
          if (readingComponents["lTime"] === 1.5) {
            if (report.getReport.languageId === 2) {
              newLComment = "與其他學生相比，閱讀速度較快；能夠自信地回答字面上的問題。";
            } else {
              newLComment = "Good reading speed relative to other students; confident to answer literal style of questions.";
            }
          } else {
            if (report.getReport.languageId === 2) {
              newLComment = "與其他學生相比，閱讀速度快；能夠回答字面上的問題。";
            } else {
              newLComment = "Fast reading speed relative to other students; able to answer literal style of questions.";
            }
          }
          break;
        case 5:
          if (report.getReport.languageId === 2) {
            newLComment = "與其他學生相比，閱讀速度快；能夠自信地回答字面上的問題。";
          } else {
            newLComment = "Fast reading speed relative to other students; confident to answer literal style of questions.";
          }
          break;
        default:
          if (report.getReport.languageId === 2) {
            newLComment = "意外的字面值。";
          } else {
            newLComment = "Unexpected literal value.";
          }
      }
      setLComment(newLComment);
      let newIComment = "";
      switch (readingComponents["inferential"]) {
        case 1:
          if (report.getReport.languageId === 2) {
            newIComment = "閱讀速度相對於其他學生較慢；難以回答推理型問題。";
          } else {
            newIComment = "Reading speed is slow relative to other students; difficulty experienced answering inferential style of questions.";
          }
          break;
        case 1.5:
          if (report.getReport.languageId === 2) {
            newIComment = "與其他學生相比，閱讀速度較快；但在回答推理型問題時遇到困難。";
          } else {
            newIComment = "Good reading speed relative to other students; difficulty experienced answering inferential style of questions.";
          }
          break;
        case 2:
          if (report.getReport.languageId === 2) {
            newIComment = "與其他學生相比，閱讀速度較快；但在回答推理型問題時遇到困難。";
          } else {
            newIComment = "Fast reading speed relative to other students; difficulty experienced answering inferential style of questions.";
          }
          break;
        case 2.5:
          if (report.getReport.languageId === 2) {
            newIComment = "閱讀速度相對於其他學生較慢；能夠回答推理型問題。";
          } else {
            newIComment = "Reading speed is slow relative to other students; able to answer inferential style of questions.";
          }
          break;
        case 3:
          if (report.getReport.languageId === 2) {
            newIComment = "與其他學生相比，閱讀速度較快；能夠回答推理型問題。";
          } else {
            newIComment = "Good reading speed relative to other students; able to answer inferential style of questions.";
          }
          break;
        case 3.5:
          if (report.getReport.languageId === 2) {
            newIComment = "與其他學生相比，閱讀速度快；能夠回答推理型問題。";
          } else {
            newIComment = "Fast reading speed relative to other students; able to answer inferential style of questions.";
          }
          break;
        case 4:
          if (report.getReport.languageId === 2) {
            newIComment = "閱讀速度相對於其他學生較慢；有信心回答推理型問題。";
          } else {
            newIComment = "Reading speed is slow relative to other students; confident to answer inferential style of questions.";
          }
          break;
        case 4.5:
          if (report.getReport.languageId === 2) {
            newIComment = "與其他學生相比，閱讀速度較快；能夠自信地回答推理型問題。";
          } else {
            newIComment = "Good reading speed relative to other students; confident to answer inferential style of questions.";
          }
          break;
        case 5:
          if (report.getReport.languageId === 2) {
            newIComment = "與其他學生相比，閱讀速度快；能夠自信地回答推理型問題。";
          } else {
            newIComment = "Fast reading speed relative to other students; confident to answer inferential style of questions.";
          }
          break;
        default:
          if (report.getReport.languageId === 2) {
            newIComment = "出乎意料的推斷價值。 ";
          } else {
            newIComment = "Unexpected inferential value.";
          }
      }
      setIComment(newIComment);

      const radarData = [
        {
          ...report.getReport.languageId === 2 ? { subject: "閱讀速度與分析" } : { subject: "Reading Speed & Analysis" },
          A: 5 + readingComponents["total"],
        },
      ];
      const mathResults = results
        .filter((r) => r.psType === "Mathematics")
        .map((r) => {
          if (r.strand === "Overall") {
            radarData.push({
              ...report.getReport.languageId === 2 ? { subject: "數學能力" } : { subject: "Mathematical Ability" },
              A: Math.floor(r.score / 10) + 5,
            });
          }
          return {
            ...r,
            ...report.getReport.languageId === 2 ? { name: TopicTranslations[r.strand] } : { name: r.strand },
            unused: 100,
          };
        });
      const tsResults = results
      .filter((r) => r.psType === "Thinking Skills")
      .map((r) => {
        if (r.strand === "Overall") {
          radarData.push({
            ...report.getReport.languageId === 2 ? { subject: "思考技能" } : { subject: "Thinking Skills" },
            A: Math.floor(r.score / 10) + 5,
          });
        }
        return {
          ...r,
          ...report.getReport.languageId === 2 ? { name: TopicTranslations[r.strand] } : { name: r.strand },
          unused: 100,
        };
      });
      const msResults = results
        .filter((r) => r.psType === "Method & Strategy")
        .map((r) => {
          if (r.strand === "Overall") {
            radarData.push({
              ...report.getReport.languageId === 2 ? { subject: "方法與策略" } : { subject: "Method & Strategy" },
              A: Math.floor(r.score / 10) + 5,
            });
          }
          return {
            ...r,
            ...report.getReport.languageId === 2 ? { name: TopicTranslations[r.strand] } : { name: r.strand },
            unused: 100,
          };
        });
      const gkResults = results
        .filter((r) => r.psType === "General Knowledge")
        .map((r) => {
          if (r.strand === "General Knowledge") {
            radarData.push({
              ...report.getReport.languageId === 2 ? { subject: "常識與推理" } : { subject: "General Knowledge & Reasoning" },
              A: Math.floor(r.score / 10) + 5,
            });
          }
          return {
            ...r,
            ...report.getReport.languageId === 2 ? { name: TopicTranslations[r.strand] } : { name: r.strand },
            unused: 200,
          };
        });
      let saResults = results
        .filter((r) => r.psType === "Speed & Accuracy")
        .map((r) => {
          if (r.strand === "Overall") {
            radarData.push({
              ...report.getReport.languageId === 2 ? { subject: "速度與精準度" } : { subject: "Speed & Accuracy" },
              A: Math.floor(r.score / 10) + 5,
            });
          }
          return {
            ...r,
            ...report.getReport.languageId === 2 ? { name: TopicTranslations[r.strand] } : { name: r.strand },
            unused: 100,
          };
        });
      const fcResults = results
        .filter((r) => r.psType === "Focus & Concentration")
        .map((r) => {
          radarData.push({
            ...report.getReport.languageId === 2 ? { subject: "毅力" } : { subject: "Perseverance" },
            A: Math.floor(r.score / 10) + 5,
          });
          return {
            ...r,
            ...report.getReport.languageId === 2 ? { name: TopicTranslations[r.strand] } : { name: r.strand },
            unused: 100,
          };
        });

      let infSittingData = {};
      let litSittingData = {};
      let isDefault = iTimes.find(
        (i) => i.studentId === report.getReport.Student.id
      ).isDefault;
      readingResults.forEach((r) => {
        const strand = r.strand.split("|");
        if (strand[0] === "Literal Analysis") {
          if (r.studentId in litSittingData) {
            if (strand[1] === "Mark") {
              litSittingData[r.studentId].correct = r.score;
            } else {
              litSittingData[r.studentId].speed = r.score;
            }
          } else {
            litSittingData[r.studentId] = {
              studentId: r.studentId,
            };
            if (strand[1] === "Mark") {
              litSittingData[r.studentId].correct = r.score;
            } else {
              litSittingData[r.studentId].speed = r.score;
            }
          }
        } else {
          if (r.studentId in infSittingData) {
            if (strand[1] === "Mark") {
              infSittingData[r.studentId].correct = r.score;
            } else {
              infSittingData[r.studentId].speed = r.score;
            }
          } else {
            infSittingData[r.studentId] = {
              studentId: r.studentId,
            };
            if (strand[1] === "Mark") {
              infSittingData[r.studentId].correct = r.score;
            } else {
              infSittingData[r.studentId].speed = r.score;
            }
          }
        }
      });
      sortedResults.Reading = {
        passageData: [
          {
            ...report.getReport.languageId === 2 ? { name: "字面分析" } : { name: "Literal Analysis" },
            sittingData: [...Object.values(litSittingData)],
          },
          {
            ...report.getReport.languageId === 2 ? { name: "推斷分析" } : { name: "Inferential Analysis" },
            sittingData: [...Object.values(infSittingData)],
          },
        ],
      };
      function compare(a, b) {
        if (a.strand === "Overall") {
          return 1;
        } else if (b.strand === "Overall") {
          return -1;
        } else {
          return a.strand - b.strand;
        }
      }

      function reverseCompare(a, b) {
        if (a.strand === "Overall") {
          return 1;
        } else if (b.strand === "Overall") {
          return -1;
        } else {
          return b.strand - a.strand;
        }
      }

      if (mathResults.length > 0) {
        sortedResults.Mathematics = [...mathResults.sort(compare)];
      } else if (tsResults.length > 0) {
        sortedResults["Thinking Skills"] = [...tsResults.sort(compare)];
      }
      
      sortedResults["Method & Strategy"] = [...msResults.sort(compare)];
      sortedResults["General Knowledge"] = [...gkResults];
      sortedResults["Speed & Accuracy"] = [...saResults.sort(reverseCompare)];
      sortedResults["Focus & Concentration"] = [...fcResults.sort(compare)];
      setPlacementData({ ...sortedResults, isDefault });
      console.log(sortedResults);
      setOverall(radarData);
      // setReportName(report.getReport.testName);
      // setPayment(
      //   `https://problemsolving.north-shore.com.au/payment?student_number=${report.getReport.Student.login}`
      // );
      // console.log(report.getReport.Student);
      setGivenname(report.getReport.Student.givenname);
      setStudentName(
        `${report.getReport.Student.givenname} ${report.getReport.Student.surname}`
      );
      setStudentId(report.getReport.Student.id);
    },
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
  });

  useEffect(() => {
    if (!userRole) getRole();
    if (location.pathname === `/report/${program}`) {
      getReport({
        variables: {
          program_id: Number(program),
          student_id: Number(JSON.parse(user).student_id),
        },
      });
    } else if (location.pathname === `/report/${program}/${id}`) {
      getReport({
        variables: {
          program_id: Number(program),
          student_id: Number(id),
        },
      });
    }

    const today = new Date();
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    setTodayDate(
      `${today.getDate()} ${months[today.getMonth()]} ${today.getFullYear()}`
    );
  }, []);

  function toTitleCase(str) {
    return str
      .toLowerCase()
      .split(" ")
      .map(function (word) {
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(" ");
  }

  return (
    <>
      <Box sx={{ margin: 3 }}>
        <Stack direction="row" justifyContent="space-between">
          {user &&
          ((JSON.parse(user).student_region &&
            JSON.parse(user).student_region === 8) ||
            (JSON.parse(user).region_id &&
              JSON.parse(user).region_id === 8)) ? (
            <img
              src={altlogo}
              alt="SolverEdge Logo"
              style={{ marginRight: "32px", height: "60px", maxWidth: "100px" }}
            />
          ) : (
            <img
              src={logo}
              alt="NSCC Logo"
              style={{ marginRight: "32px", height: "60px", maxWidth: "100px" }}
            />
          )}
          <Typography variant="h4" component="div">
            {languageId === 2 ? "解難能力評估報告" : "Problem Solving Assessment Report"}
          </Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between">
          <Box />
          <Typography variant="h5" component="div">
            {todayDate}
          </Typography>
        </Stack>
        <Typography variant="body1" component="div" sx={{ fontSize: 20 }}>
          {languageId === 2 ? "親愛的" : "Dear"} {toTitleCase(studentName)},
        </Typography>
        <br />
        <Typography variant="body1" component="div" sx={{ fontSize: 20 }}>
          {languageId === 2 ? `
            感謝您使用 SolverEdge 完成解難能力評估。
            本報告建議在螢幕尺寸為 10 吋或以上的桌上型電腦/筆記型電腦或其他裝置上查看。
            如果下方的成績評分錶無法正確加載，我們建議您將 PDF 報告下載到桌上型電腦/筆記型電腦上，並使用 PDF 閱讀器軟體開啟。 
          ` : `
            Thank you for sitting the Problem Solving Assessment with SolverEdge.\
            This report is best viewed on desktop computer/laptop or device with\
            screen size of 10 inches or more. If the achievement scale below does\
            not load correctly, we recommend that you download the PDF report onto\
            a desktop computer/laptop and open it using PDF reader software.\
          `}
        </Typography>
        <br />
        <br />
        <br />
        <Typography variant="h5" component="div">
          {languageId === 2 ? "你的成就" : "Your Achievement"}
        </Typography>
        <Stack direction="row" justifyContent="space-between">
          <Box />
          <RadarGraph data={overall} type="average" languageId={languageId} />
          <Box />
        </Stack>
        <br />
        {trainings.length > 0 &&
        user &&
        ((JSON.parse(user).student_region &&
          JSON.parse(user).student_region === 8) ||
          (JSON.parse(user).region_id && JSON.parse(user).region_id === 8)) ? (
          <>
            <Typography variant="body1" component="div" sx={{ fontSize: 20 }}>
              { languageId === 2 ? `透過參加我們的解難能力培訓，可以提高 ${toTitleCase(givenname)} 的解難能力。此培訓包含以下培訓模組： ` : 
                `${toTitleCase(givenname)}
                ${toTitleCase(givenname).slice(-1) === "s" ? "'" : "'s"} problem
                solving level could be raised through enrolling in our Problem
                Solving training which will comprise of the following training
                modules:
              `}
            </Typography>
            <Typography variant="body1" component="div" sx={{ fontSize: 16 }}>
              <ul>
                {trainings.sort().map((t) => (
                  <li>{t}</li>
                ))}
              </ul>
            </Typography>
          </>
        ) : (
          <></>
        )}
        <br />
        <Typography
          variant="body1"
          component="div"
          sx={{ pageBreakBefore: "always", fontSize: 20 }}
        >
          {languageId === 2 ? "接下來的幾頁將提供更詳細的分析。" : "In the following pages, you will find more detailed analysis."}
        </Typography>
        <br />
        <Typography variant="h5" component="div">
          1. {languageId === 2 ? "閱讀速度與分析" : "Reading Speed & Analysis"}
        </Typography>
        <br />
        {placementData && (
          <Grid container spacing={2}>
            {placementData.Reading.passageData.map((p) => {
              return (
                <Grid item xs={6}>
                  <ScatterGraph
                    passageData={p}
                    student_id={studentId}
                    isDefault={placementData.isDefault}
                    student_name={toTitleCase(givenname)}
                    languageId={languageId}
                  />
                </Grid>
              );
            })}
          </Grid>
        )}
        <br />
        <Typography variant="body1" component="div" sx={{ fontSize: 20 }}>
          <strong>{languageId === 2 ? "閱讀速度： " : "Reading Speed: "}</strong> 
          { languageId === 2 ? `
            指學生閱讀和理解文本的速度。如果閱讀速度慢，學生可能難以在規定時間內回答問題。如果閱讀速度過快，學生可能難以有效理解和消化文字內容。
          `: `
            The speed at which a student can read
            and process texts presented to them. If reading speed is slow, the
            student may struggle to be able to answer questions within the given
            amount of time. If reading speed is too fast, the student may struggle
            to effectively process and obtain a proper understanding of the texts.
          `}
        </Typography>
        <Typography variant="body1" component="div" sx={{ fontSize: 20 }}>
          <strong>{languageId === 2 ? "字面分析： " : "Literal Analysis: "}</strong> 
          {languageId === 2 ? `
            指學生處理和理解文本中事實和細節的能力。涉及字面分析的問題要求學生回憶文本中的信息才能回答問題。
          `: `
            The ability of a student to process
            and understand facts and details in a text. Questions involving
            literal analysis will require students to recall information from the
            text in order to be able to answer the question.
          `}
        </Typography>
        <Typography variant="body1" component="div" sx={{ fontSize: 20 }}>
          <strong>{languageId === 2 ? "推理分析： " : "Inferential Analysis: "}</strong> 
          {languageId === 2 ? `
            指學生解讀文本訊息並得出合理結論的能力。涉及推理分析的問題要求學生不僅要理解文本的事實和細節，還要推斷背後的意義。
          ` : `
            The ability of a student to
            interpret information in a text and make reasonable conclusions.
            Questions involving inferential analysis will require students to go
            beyond understanding the facts and details of the text and infer the
            meaning behind them.
          `}
        </Typography>
        {!placementData?.isDefault && (
          <>
            <br />
            <br />
            <Typography variant="body1" component="div" sx={{ fontSize: 20 }}>
              {languageId === 2 ? "您孩子在閱讀評估中的表現和行為可以總結如下： " : "Your child's performance and behaviour in the Reading Assessment can be summarised as follows:"}
            </Typography>
            <Typography variant="body1" component="div" sx={{ fontSize: 20 }}>
              <strong>{languageId === 2 ? "字面分析：" : "Literal Analysis:"}</strong>
            </Typography>
            <Typography variant="body1" component="div" sx={{ fontSize: 20 }}>
              {lComment}
            </Typography>
            <br />
            <Typography variant="body1" component="div" sx={{ fontSize: 20 }}>
              <strong>{languageId === 2 ? "推理分析：" : "Inferential Analysis:"}</strong>
            </Typography>
            <Typography variant="body1" component="div" sx={{ fontSize: 20 }}>
              {iComment}
            </Typography>
          </>
        )}
        <br />
        {
          placementData && placementData["Mathematics"] ? (
            <>
              <Typography
                variant="h5"
                component="div"
                sx={{ pageBreakBefore: "always" }}
              >
                2. {languageId === 2 ? "數學能力" : "Mathematical Ability"}
              </Typography>
              <Stack direction="row" justifyContent="space-between">
                <Box />
                {placementData != null && (
                  <BarGraph
                    data={placementData["Mathematics"]}
                    student_name={toTitleCase(givenname)}
                    languageId={languageId}
                  />
                )}
                <Box />
              </Stack>
              <Typography variant="body1" component="div" sx={{ fontSize: 20 }}>
                <strong>{languageId === 2 ? "數學能力：" : "Mathematical Ability: "}</strong> {languageId === 2 ? 
                "該學生解答數學題的能力。" : "The student's ability to do Mathematics questions."}
              </Typography>
            </>
          ) : <></>
        }
        {
          placementData && placementData["Thinking Skills"] ? (
            <>
              <Typography
                variant="h5"
                component="div"
                sx={{ pageBreakBefore: "always" }}
              >
                2. {languageId === 2 ? "思考技能" : "Thinking Skills"}
              </Typography>
              <Stack direction="row" justifyContent="space-between">
                <Box />
                {placementData != null && (
                  <BarGraph
                    data={placementData["Thinking Skills"]}
                    student_name={toTitleCase(givenname)}
                    languageId={languageId}
                  />
                )}
                <Box />
              </Stack>
              <Typography variant="body1" component="div" sx={{ fontSize: 20 }}>
                <strong>{languageId === 2 ? "思考技能：" : "Thinking Skills: "}</strong> 
                {languageId === 2 ? "學生運用批判性思考和邏輯思考解決問題的能力。" : "The student's ability to do use Critical and Logical thinking to solve problems."}
              </Typography>
            </>
          ) : <></>
        }
        <br />
        <Typography
          variant="h5"
          component="div"
          sx={{ pageBreakBefore: "always" }}
        >
          3. {languageId === 2 ? "策略與方法" : "Method & Strategy"}
        </Typography>
        <Stack direction="row" justifyContent="space-between">
          <Box />
          {placementData != null && (
            <BarGraph
              data={placementData["Method & Strategy"]}
              student_name={toTitleCase(givenname)}
              languageId={languageId}
            />
          )}
          <Box />
        </Stack>
        <Typography variant="body1" component="div" sx={{ fontSize: 20 }}>
          <strong>{languageId === 2 ? "方法與策略： " : "Method & Strategy: "}</strong> 
          {languageId === 2 ? "運用多種策略解決難題。學生掌握的策略越多，解決問題的能力就越強。" : `
            The use of various strategies to
            solve challenging problem questions. The more strategies with which
            students are familiar, the better their problem solving capabilities.
          `}
        </Typography>
        <br />
        <Typography
          variant="h5"
          component="div"
          sx={{ pageBreakBefore: "always" }}
        >
          4. {languageId === 2 ? "常識與推理" : "General Knowledge & Reasoning"}
        </Typography>
        <Stack direction="row" justifyContent="space-between">
          <Box />
          {placementData != null && (
            <BarGraph
              data={placementData["General Knowledge"]}
              student_name={toTitleCase(givenname)}
              languageId={languageId}
            />
          )}
          <Box />
        </Stack>
        <Typography variant="body1" component="div" sx={{ fontSize: 20 }}>
          <strong>{languageId === 2 ? "常識： " : "Common Knowledge: "}</strong> 
          {languageId === 2 ? "基於與日常生活相關的事實的知識程度。" : "The extent of knowledge based on facts relating to daily life."}
        </Typography>
        <Typography variant="body1" component="div" sx={{ fontSize: 20 }}>
          <strong>{languageId === 2 ? "推理能力： " : "Reasoning: "}</strong> 
          {languageId === 2 ? "學生運用邏輯和推理得出結論的能力。" : "The ability of a student to use logic and reason to make conclusions."}
        </Typography>
        <br />
        <br />
        <Typography variant="h5" component="div">
          5. {languageId === 2 ? "速度與精準度" : "Speed & Accuracy"}
        </Typography>
        <Stack direction="row" justifyContent="space-between">
          <Box />
          {placementData != null && (
            <BarGraph
              data={placementData["Speed & Accuracy"]}
              student_name={toTitleCase(givenname)}
              languageId={languageId}
            />
          )}
          <Box />
        </Stack>
        <Typography variant="body1" component="div" sx={{ fontSize: 20 }}>
          <strong>{languageId === 2 ? "速度與準確性： " : "Speed & Accuracy: "}</strong> 
          {languageId === 2 ? `
            檢視學生快速且準確地進行基本計算與解答問題的能力。速度與準確度部分的題目通常包含簡單的計算題或簡短的習題，
            一般不需要太多步驟即可解答。題目可能涉及基礎但非深入的概念性知識。
          ` : ` The ability of a student to perform
            basic calculations and process questions quickly as well as
            accurately. Questions in Speed & Accuracy involve simple calculation
            questions or short problems that typically do not require many steps
            to solve. The questions may involve basic but not in-depth conceptual
            knowledge.
          `}
        </Typography>
        <br />
        <br />
        <Typography variant="h5" component="div">
          6. {languageId === 2 ? "毅力" : "Perseverance"}
        </Typography>
        <Stack direction="row" justifyContent="space-between">
          <Box />
          {placementData != null && (
            <BarGraph
              data={placementData["Focus & Concentration"]}
              student_name={toTitleCase(givenname)}
              languageId={languageId}
            />
          )}
          <Box />
        </Stack>
        {/* <Typography variant="body1" component="div">
          <strong>Focus:</strong> The ability of a student to maintain concentration on a task. Students with good focus will stay on one task before moving onto the next task.
        </Typography> */}
        <Typography variant="body1" component="div" sx={{ fontSize: 20 }}>
          <strong>{languageId === 2 ? "毅力： " : "Perseverance: "}</strong> 
          {languageId === 2 ? "指即使面對障礙或困難，仍能堅持不懈地朝向目標努力的品質。具有高度毅力的學生能夠保持專注，並致力於解決看似不可能的難題。" : `
            The quality of continuing to work
            towards a goal despite facing obstacles or difficulties. Students who
            are high in perseverance have the ability to stay focused and
            committed to resolving challenging questions, even when they seem
            impossible.
          `}
        </Typography>
        <br />
        <Typography
          variant="h4"
          component="div"
          sx={{ pageBreakBefore: "always" }}
        >
          {languageId === 2 ? "指導" : "Guide to Understanding Your Result"}
        </Typography>
        <RadarReportInstructions languageId={languageId} hasMaths={!!placementData?.Mathematics} />
        <ReadingReportInstructions languageId={languageId} />
        <Box sx={{ pageBreakAfter: "always" }} />
        <BarReportInstructions languageId={languageId} />
      </Box>
    </>
  );
};

export default PlacementReport;
